import { useEffect, useState } from 'react';
import { db } from '@/lib/db';
import type { Ace, Course, MemberPublic } from '@/lib/db/types';
import { Sheet } from '@/components/shared/Sheet';
import { Avatar } from '@/components/shared/Avatar';

interface Props {
  onClose: () => void;
  onOpenProfile?: (id: string) => void;
}

export function HoleInOneWall({ onClose, onOpenProfile }: Props) {
  const [aces, setAces] = useState<Ace[]>([]);
  const [members, setMembers] = useState<Record<string, MemberPublic>>({});
  const [courses, setCourses] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const [aceList, people, courseList] = await Promise.all([
        db.aces.list(),
        db.members.list({ limit: 200 }),
        db.courses.list(),
      ]);
      setAces(aceList);
      setMembers(Object.fromEntries(people.map((m) => [m.id, m])));
      setCourses(Object.fromEntries(courseList.map((c) => [c.id, c.name])));
      setLoading(false);
    })();
  }, []);

  return (
    <Sheet title="Hole-in-One Wall" onClose={onClose}>
      <div className="space-y-3 px-4 pb-8 pt-2">
        <p className="text-xs leading-snug text-ink-soft">The permanent club ace registry. Every hole-in-one earns +500 Eagle Points.</p>
        {loading && <div className="text-sm text-mute">Loading…</div>}
        {!loading && aces.length === 0 && (
          <div className="rounded-xl bg-cream p-6 text-center text-sm text-mute">No aces recorded yet.</div>
        )}
        {aces.map((ace) => {
          const person = members[ace.member_id];
          return (
            <div key={ace.id} className="flex items-center gap-3 rounded border-2 border-gold/40 bg-gradient-to-r from-gold/10 to-paper p-3">
              <button type="button" onClick={() => person && onOpenProfile?.(person.id)}>
                {person ? <Avatar member={person} size={44} /> : <div className="h-11 w-11 rounded-full bg-navy/10" />}
              </button>
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => person && onOpenProfile?.(person.id)} className="font-serif font-bold text-navy">{person?.name || 'Member'}</button>
                <div className="text-sm text-ink-soft">Hole {ace.hole_num} · {ace.course_id ? courses[ace.course_id] : ace.notes || 'Course'}</div>
                <div className="text-[10px] font-bold tracking-wider text-gold-deep">+500 POINTS</div>
              </div>
              <div className="text-2xl">🦅</div>
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}
